import React, { ReactNode, useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import ContextPicker from './ContextPicker';
import RegisterActions from './RegisterActions';
import BottomNav, { BOTTOM_NAV_HEIGHT } from './BottomNav';
import AccountMenu from './AccountMenu';
import { GuestBanner } from '../auth/GuestBanner';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { tokens } from '../../theme/tokens';

interface LayoutProps {
  children: ReactNode;
}

/**
 * The signed-in frame. Desktop: sidebar, a top bar with the context in view
 * and "Registrar". Phone: the context and the avatar at the top, the bottom
 * navigation with "+" in the middle.
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation('navigation');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedContext } = useFinancialContexts();
  const [registerAnchor, setRegisterAnchor] = useState<HTMLElement | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const inGroup = Boolean(selectedContext && selectedContext.type !== 'personal');

  const openRegister = (anchor: HTMLElement | null) => {
    setRegisterAnchor(anchor);
    setRegisterOpen(true);
  };
  const closeRegister = () => setRegisterOpen(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* the column keeps the sidebar's white and its edge all the way down a long page */}
      {!isMobile && (
        <Box sx={{ flexShrink: 0, bgcolor: tokens.surface, borderRight: `1px solid ${tokens.line}` }}>
          <Sidebar />
        </Box>
      )}

      {/* minWidth 0 keeps wide content (tables, chips) inside the screen instead of widening the page */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          component='header'
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: { xs: 2, md: 4 },
            py: { xs: 1.25, md: 1.75 },
            bgcolor: tokens.surface,
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <ContextPicker />
          {isMobile ? (
            <AccountMenu />
          ) : (
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={(event) => openRegister(event.currentTarget)}
              aria-haspopup='menu'
              sx={{ minHeight: 44 }}
            >
              {inGroup ? t('shell.registerInGroup') : t('shell.register')}
            </Button>
          )}
        </Box>

        <Box
          component='main'
          sx={{
            flex: 1,
            width: '100%',
            // fills a full-HD screen; past that, centred so lines don't run on forever
            maxWidth: 1680,
            mx: 'auto',
            px: { xs: 2, md: 4 },
            pt: { xs: 2.5, md: 3.5 },
            pb: { xs: `${BOTTOM_NAV_HEIGHT + 28}px`, md: 7 },
          }}
        >
          <GuestBanner />
          {children}
        </Box>
      </Box>

      {isMobile && <BottomNav onRegister={() => openRegister(null)} />}
      <RegisterActions anchor={registerAnchor} open={registerOpen} onClose={closeRegister} asSheet={isMobile} />
    </Box>
  );
};

export default Layout;
